import { useEffect, useMemo, useState } from 'react';
import { Plus, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { ErrorMessage } from '@/components/ui/error-message';
import { Spinner } from '@/components/ui/spinner';
import {
  useAssessmentDefaultsQuery,
  useSaveAssessmentDefaultsMutation,
  weightsSum,
  weightsSumToOne,
  type AssessmentDefaultsValue,
  type CriterionScoreType,
  type DefaultCriterion,
  type DefaultDomain,
} from '@/lib/api/assessment-defaults';
import { cn } from '@/lib/utils/cn';
import { formatDateTime } from '@/lib/utils/format';

/**
 * Firm settings → Assessment defaults (T-0219 / E20-F01).
 *
 * One screen, inline editing: the health domains, the readiness criteria
 * with their weights and the Go / Conditional thresholds, the finding
 * types and the checkpoint scoring scale every new engagement starts with.
 * The sum-to-1 rule is shown next to the weights and a set that does not
 * sum to 1 cannot be saved (E20 rule 3: refused, never normalised). The API
 * applies the same rules, so a refusal from it is shown as it comes.
 *
 * Theme tokens and the existing primitives only (Card, Button, the hub's
 * type scale); no new primitives.
 */

const SCORE_TYPES: ReadonlyArray<{ value: CriterionScoreType; label: string }> = [
  { value: 'numeric_0_10', label: 'Numeric 0–10' },
  { value: 'pass_fail', label: 'Pass / fail' },
  { value: 'ryg', label: 'Red / yellow / green' },
];

const KNOWN_SEVERITIES = ['critical', 'high', 'medium', 'low', 'info'] as const;
const KNOWN_CHECKPOINT_SCORE_TYPES: ReadonlyArray<{ value: string; label: string }> = [
  { value: 'numeric', label: 'Numeric' },
  { value: 'pass_fail', label: 'Pass / fail' },
  { value: 'ryg', label: 'Red / yellow / green' },
];

const INPUT_CLASS =
  'h-8 w-full rounded-card border border-ink-100 bg-paper-0 px-2 text-body-sm text-ink-900 placeholder:text-ink-500 focus:outline-none focus:ring-1 focus:ring-accent-amber-600 disabled:opacity-50';
const SECTION_HINT = 'text-meta text-ink-500';

function cloneValue(v: AssessmentDefaultsValue): AssessmentDefaultsValue {
  return {
    domains: v.domains.map((d) => ({ ...d })),
    criteria: v.criteria.map((c) => ({ ...c })),
    thresholds: { ...v.thresholds },
    finding_types: {
      categories: [...v.finding_types.categories],
      severities: [...v.finding_types.severities],
    },
    scoring_scale: {
      score_types: [...v.scoring_scale.score_types],
      numeric: { ...v.scoring_scale.numeric },
      pass_fail: [...v.scoring_scale.pass_fail],
      ryg: [...v.scoring_scale.ryg],
    },
  };
}

/** Every reason the draft cannot be saved, in the order the screen shows them. */
export function draftProblems(draft: AssessmentDefaultsValue): string[] {
  const problems: string[] = [];
  if (draft.domains.length === 0) problems.push('Add at least one health domain.');
  if (draft.domains.some((d) => d.name.trim().length === 0)) {
    problems.push('Every health domain needs a name.');
  }
  const domainNames = draft.domains.map((d) => d.name.trim().toLowerCase());
  if (new Set(domainNames).size !== domainNames.length) {
    problems.push('Health domain names must be unique.');
  }
  if (draft.criteria.length === 0) problems.push('Add at least one readiness criterion.');
  if (draft.criteria.some((c) => c.name.trim().length === 0 || c.category.trim().length === 0)) {
    problems.push('Every criterion needs a category and a name.');
  }
  const criterionNames = draft.criteria.map((c) => c.name.trim().toLowerCase());
  if (new Set(criterionNames).size !== criterionNames.length) {
    problems.push('Criterion names must be unique.');
  }
  if (draft.criteria.some((c) => !(c.weight > 0 && c.weight <= 1))) {
    problems.push('Each weight must be above 0 and at most 1.');
  }
  if (draft.criteria.length > 0 && !weightsSumToOne(draft.criteria)) {
    problems.push(
      `Criterion weights total ${weightsSum(draft.criteria).toFixed(4)}, not 1. Adjust the weights of: ${draft.criteria
        .map((c) => `${c.name.trim() || 'unnamed'} (${c.weight})`)
        .join(', ')}.`,
    );
  }
  if (!(draft.thresholds.conditional < draft.thresholds.go)) {
    problems.push('The Conditional threshold must be below the Go threshold.');
  }
  if (draft.finding_types.categories.some((c) => c.trim().length === 0)) {
    problems.push('Every finding category needs a name.');
  }
  if (draft.finding_types.categories.length === 0)
    problems.push('Add at least one finding category.');
  if (draft.finding_types.severities.length === 0) problems.push('Keep at least one severity.');
  if (draft.scoring_scale.score_types.length === 0) problems.push('Keep at least one score type.');
  const n = draft.scoring_scale.numeric;
  if (!(n.min < n.divergent_at && n.divergent_at < n.pass_at && n.pass_at <= n.max)) {
    problems.push('The numeric scale must read min < divergent < pass <= max.');
  }
  return problems;
}

export function AssessmentDefaultsEditor() {
  const query = useAssessmentDefaultsQuery();
  const save = useSaveAssessmentDefaultsMutation();
  const [draft, setDraft] = useState<AssessmentDefaultsValue | null>(null);
  const [savedAt, setSavedAt] = useState<string | null>(null);

  useEffect(() => {
    if (query.data) setDraft(cloneValue(query.data));
  }, [query.data]);

  const problems = useMemo(() => (draft ? draftProblems(draft) : []), [draft]);
  const sum = draft ? weightsSum(draft.criteria) : 0;
  const sumOk = draft ? weightsSumToOne(draft.criteria) : true;
  const dirty =
    draft !== null &&
    query.data !== undefined &&
    JSON.stringify(draft) !== JSON.stringify(cloneValue(query.data));

  if (query.isLoading) {
    return (
      <div className="flex items-center gap-2 text-body-sm text-ink-500">
        <Spinner /> Loading assessment defaults…
      </div>
    );
  }
  if (query.error) return <ErrorMessage error={query.error} />;
  if (!draft || !query.data) return null;

  const data = query.data;

  function update(patch: (prev: AssessmentDefaultsValue) => AssessmentDefaultsValue) {
    setDraft((prev) => (prev ? patch(cloneValue(prev)) : prev));
  }

  function onSave() {
    if (!draft || problems.length > 0 || save.isPending) return;
    save.mutate(
      {
        ...draft,
        domains: draft.domains.map((d) => ({
          name: d.name.trim(),
          description: d.description && d.description.trim().length > 0 ? d.description : null,
        })),
        criteria: draft.criteria.map((c) => ({
          ...c,
          name: c.name.trim(),
          category: c.category.trim(),
          description: c.description && c.description.trim().length > 0 ? c.description : null,
        })),
        finding_types: {
          categories: draft.finding_types.categories.map((c) => c.trim()),
          severities: draft.finding_types.severities,
        },
      },
      { onSuccess: () => setSavedAt(new Date().toISOString()) },
    );
  }

  return (
    <div className="space-y-5">
      <header className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-h1 font-semibold text-ink-900">Assessment defaults</h1>
          <p className="text-meta text-ink-500">
            What every new engagement starts with. Engagements keep what they were given; a lead
            adjusts per engagement from Health &amp; Readiness.
          </p>
          <p className="mt-1 text-meta text-ink-500">
            Version {data.version}
            {data.updated_at
              ? ` · saved ${formatDateTime(data.updated_at)}`
              : ' · the code defaults'}
            {data.updated_by_name ? ` by ${data.updated_by_name}` : ''}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            type="button"
            size="sm"
            variant="outline"
            disabled={!dirty || save.isPending}
            onClick={() => setDraft(cloneValue(data))}
          >
            Discard changes
          </Button>
          <Button
            type="button"
            size="sm"
            disabled={!dirty || problems.length > 0 || save.isPending}
            onClick={onSave}
          >
            {save.isPending ? 'Saving…' : 'Save defaults'}
          </Button>
        </div>
      </header>

      {problems.length > 0 ? (
        <div
          role="status"
          className="rounded-card border border-accent-amber-600 bg-paper-100 p-3 text-body-sm text-ink-900"
        >
          <p className="font-medium">Cannot save yet</p>
          <ul className="mt-1 list-disc space-y-0.5 pl-5 text-ink-700">
            {problems.map((p) => (
              <li key={p}>{p}</li>
            ))}
          </ul>
        </div>
      ) : null}
      {save.error ? <ErrorMessage error={save.error} /> : null}
      {savedAt && !dirty && !save.error ? (
        <p className="text-meta text-posture-healthy">Saved {formatDateTime(savedAt)}.</p>
      ) : null}

      {/* Health domains */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center justify-between gap-4">
            <span>Health domains</span>
            <span className="text-meta font-normal text-ink-500">
              {draft.domains.length} domain{draft.domains.length === 1 ? '' : 's'}
            </span>
          </CardTitle>
          <p className={SECTION_HINT}>
            The domains a new engagement rates. Order here is the order on the rail.
          </p>
        </CardHeader>
        <CardContent className="space-y-2">
          {draft.domains.map((d, i) => (
            <div key={i} className="grid grid-cols-[minmax(0,1fr)_minmax(0,1.4fr)_auto] gap-2">
              <input
                aria-label={`Domain ${i + 1} name`}
                className={INPUT_CLASS}
                value={d.name}
                placeholder="Domain name"
                onChange={(e) =>
                  update((p) => {
                    p.domains[i] = { ...(p.domains[i] as DefaultDomain), name: e.target.value };
                    return p;
                  })
                }
              />
              <input
                aria-label={`Domain ${i + 1} description`}
                className={INPUT_CLASS}
                value={d.description ?? ''}
                placeholder="What this domain covers (optional)"
                onChange={(e) =>
                  update((p) => {
                    p.domains[i] = {
                      ...(p.domains[i] as DefaultDomain),
                      description: e.target.value.length > 0 ? e.target.value : null,
                    };
                    return p;
                  })
                }
              />
              <RemoveButton
                label={`Remove domain ${d.name || i + 1}`}
                onClick={() =>
                  update((p) => {
                    p.domains.splice(i, 1);
                    return p;
                  })
                }
              />
            </div>
          ))}
          <AddButton
            label="Add domain"
            onClick={() =>
              update((p) => {
                p.domains.push({ name: '', description: null });
                return p;
              })
            }
          />
        </CardContent>
      </Card>

      {/* Readiness criteria */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center justify-between gap-4">
            <span>Readiness criteria</span>
            <span
              className={cn(
                'text-meta font-normal',
                sumOk ? 'text-ink-500' : 'text-posture-critical',
              )}
              data-testid="weights-sum"
            >
              Weights total {sum.toFixed(4)} of 1
            </span>
          </CardTitle>
          <p className={SECTION_HINT}>
            Grouped by category on the scorecard; the weights must sum to 1. A set that does not is
            refused, never rebalanced for you. Each criterion's description says what 100%, 50% and
            0% look like; the AI scores against it.
          </p>
        </CardHeader>
        <CardContent className="space-y-2">
          <div className="hidden grid-cols-[minmax(0,1fr)_minmax(0,1.6fr)_6rem_11rem_auto] gap-2 text-meta font-semibold uppercase tracking-wider text-ink-500 md:grid">
            <span>Category</span>
            <span>Criterion</span>
            <span>Weight</span>
            <span>Score type</span>
            <span />
          </div>
          {draft.criteria.map((c, i) => (
            <div
              key={i}
              className="grid grid-cols-1 gap-2 md:grid-cols-[minmax(0,1fr)_minmax(0,1.6fr)_6rem_11rem_auto]"
            >
              <input
                aria-label={`Criterion ${i + 1} category`}
                className={INPUT_CLASS}
                value={c.category}
                placeholder="Category"
                onChange={(e) =>
                  update((p) => {
                    p.criteria[i] = {
                      ...(p.criteria[i] as DefaultCriterion),
                      category: e.target.value,
                    };
                    return p;
                  })
                }
              />
              <input
                aria-label={`Criterion ${i + 1} name`}
                className={INPUT_CLASS}
                value={c.name}
                placeholder="Criterion"
                onChange={(e) =>
                  update((p) => {
                    p.criteria[i] = {
                      ...(p.criteria[i] as DefaultCriterion),
                      name: e.target.value,
                    };
                    return p;
                  })
                }
              />
              <input
                aria-label={`${c.name || `Criterion ${i + 1}`} weight`}
                className={cn(INPUT_CLASS, 'font-mono')}
                type="number"
                min={0}
                max={1}
                step={0.01}
                value={c.weight}
                onChange={(e) =>
                  update((p) => {
                    const next = Number(e.target.value);
                    p.criteria[i] = {
                      ...(p.criteria[i] as DefaultCriterion),
                      weight: Number.isFinite(next) ? next : 0,
                    };
                    return p;
                  })
                }
              />
              <select
                aria-label={`Criterion ${i + 1} score type`}
                className={INPUT_CLASS}
                value={c.score_type}
                onChange={(e) =>
                  update((p) => {
                    p.criteria[i] = {
                      ...(p.criteria[i] as DefaultCriterion),
                      score_type: e.target.value as CriterionScoreType,
                    };
                    return p;
                  })
                }
              >
                {SCORE_TYPES.map((t) => (
                  <option key={t.value} value={t.value}>
                    {t.label}
                  </option>
                ))}
              </select>
              <RemoveButton
                label={`Remove criterion ${c.name || i + 1}`}
                onClick={() =>
                  update((p) => {
                    p.criteria.splice(i, 1);
                    return p;
                  })
                }
              />
              <textarea
                aria-label={`Criterion ${i + 1} description`}
                className={cn(INPUT_CLASS, 'h-auto py-1.5 md:col-span-5')}
                rows={2}
                maxLength={2000}
                value={c.description ?? ''}
                placeholder="What 100%, 50% and 0% look like (the AI scores against it)"
                onChange={(e) =>
                  update((p) => {
                    p.criteria[i] = {
                      ...(p.criteria[i] as DefaultCriterion),
                      description: e.target.value.length > 0 ? e.target.value : null,
                    };
                    return p;
                  })
                }
              />
            </div>
          ))}
          <AddButton
            label="Add criterion"
            onClick={() =>
              update((p) => {
                p.criteria.push({
                  category: '',
                  name: '',
                  weight: 0.05,
                  score_type: 'numeric_0_10',
                  description: null,
                });
                return p;
              })
            }
          />

          <div className="mt-3 grid grid-cols-1 gap-3 border-t border-ink-100 pt-3 sm:grid-cols-2">
            <NumberField
              label="Go at or above (%)"
              value={draft.thresholds.go}
              min={0}
              max={100}
              onChange={(v) =>
                update((p) => {
                  p.thresholds.go = v;
                  return p;
                })
              }
            />
            <NumberField
              label="Conditional at or above (%)"
              value={draft.thresholds.conditional}
              min={0}
              max={100}
              onChange={(v) =>
                update((p) => {
                  p.thresholds.conditional = v;
                  return p;
                })
              }
            />
          </div>
          <p className={SECTION_HINT}>
            Below the Conditional threshold the verdict is No-Go. Conditional must stay below Go.
          </p>
        </CardContent>
      </Card>

      {/* Finding types */}
      <Card>
        <CardHeader>
          <CardTitle>Finding types</CardTitle>
          <p className={SECTION_HINT}>
            The categories a finding is filed under and the severities in use.
          </p>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="space-y-2">
            <p className="text-body-sm font-medium text-ink-900">Categories</p>
            {draft.finding_types.categories.map((cat, i) => (
              <div key={i} className="grid grid-cols-[minmax(0,1fr)_auto] gap-2">
                <input
                  aria-label={`Finding category ${i + 1}`}
                  className={INPUT_CLASS}
                  value={cat}
                  placeholder="Category"
                  onChange={(e) =>
                    update((p) => {
                      p.finding_types.categories[i] = e.target.value;
                      return p;
                    })
                  }
                />
                <RemoveButton
                  label={`Remove finding category ${cat || i + 1}`}
                  onClick={() =>
                    update((p) => {
                      p.finding_types.categories.splice(i, 1);
                      return p;
                    })
                  }
                />
              </div>
            ))}
            <AddButton
              label="Add category"
              onClick={() =>
                update((p) => {
                  p.finding_types.categories.push('');
                  return p;
                })
              }
            />
          </div>
          <div>
            <p className="text-body-sm font-medium text-ink-900">Severities</p>
            <div className="mt-1 flex flex-wrap gap-3">
              {KNOWN_SEVERITIES.map((sev) => (
                <label key={sev} className="flex items-center gap-1.5 text-body-sm text-ink-700">
                  <input
                    type="checkbox"
                    checked={draft.finding_types.severities.includes(sev)}
                    onChange={(e) =>
                      update((p) => {
                        const set = new Set(p.finding_types.severities);
                        if (e.target.checked) set.add(sev);
                        else set.delete(sev);
                        p.finding_types.severities = KNOWN_SEVERITIES.filter((s) => set.has(s));
                        return p;
                      })
                    }
                  />
                  {sev}
                </label>
              ))}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Scoring scale */}
      <Card>
        <CardHeader>
          <CardTitle>Scoring scale</CardTitle>
          <p className={SECTION_HINT}>
            How checkpoint criteria are scored: the score types in use and the bands of the numeric
            scale.
          </p>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="flex flex-wrap gap-3">
            {KNOWN_CHECKPOINT_SCORE_TYPES.map((t) => (
              <label key={t.value} className="flex items-center gap-1.5 text-body-sm text-ink-700">
                <input
                  type="checkbox"
                  checked={draft.scoring_scale.score_types.includes(t.value)}
                  onChange={(e) =>
                    update((p) => {
                      const set = new Set(p.scoring_scale.score_types);
                      if (e.target.checked) set.add(t.value);
                      else set.delete(t.value);
                      p.scoring_scale.score_types = KNOWN_CHECKPOINT_SCORE_TYPES.map(
                        (k) => k.value,
                      ).filter((v) => set.has(v));
                      return p;
                    })
                  }
                />
                {t.label}
              </label>
            ))}
          </div>
          <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
            <NumberField
              label="Numeric min"
              value={draft.scoring_scale.numeric.min}
              onChange={(v) =>
                update((p) => {
                  p.scoring_scale.numeric.min = v;
                  return p;
                })
              }
            />
            <NumberField
              label="Divergent at"
              value={draft.scoring_scale.numeric.divergent_at}
              onChange={(v) =>
                update((p) => {
                  p.scoring_scale.numeric.divergent_at = v;
                  return p;
                })
              }
            />
            <NumberField
              label="Pass at"
              value={draft.scoring_scale.numeric.pass_at}
              onChange={(v) =>
                update((p) => {
                  p.scoring_scale.numeric.pass_at = v;
                  return p;
                })
              }
            />
            <NumberField
              label="Numeric max"
              value={draft.scoring_scale.numeric.max}
              onChange={(v) =>
                update((p) => {
                  p.scoring_scale.numeric.max = v;
                  return p;
                })
              }
            />
          </div>
          <p className={SECTION_HINT}>
            Pass / fail reads {draft.scoring_scale.pass_fail.join(' · ')}; red / yellow / green
            reads {draft.scoring_scale.ryg.join(' · ')}.
          </p>
        </CardContent>
      </Card>
    </div>
  );
}

function NumberField({
  label,
  value,
  min,
  max,
  onChange,
}: {
  label: string;
  value: number;
  min?: number;
  max?: number;
  onChange: (next: number) => void;
}) {
  return (
    <label className="flex flex-col gap-1 text-meta font-semibold text-ink-700">
      {label}
      <input
        className={cn(INPUT_CLASS, 'font-mono font-normal')}
        type="number"
        min={min}
        max={max}
        step={1}
        value={value}
        onChange={(e) => {
          const next = Number(e.target.value);
          onChange(Number.isFinite(next) ? next : 0);
        }}
      />
    </label>
  );
}

function RemoveButton({ label, onClick }: { label: string; onClick: () => void }) {
  return (
    <button
      type="button"
      aria-label={label}
      title={label}
      onClick={onClick}
      className="inline-flex h-8 w-8 items-center justify-center rounded-card text-ink-500 transition-colors hover:bg-paper-100 hover:text-ink-900 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent-amber-600"
    >
      <X className="h-4 w-4" aria-hidden="true" />
    </button>
  );
}

function AddButton({ label, onClick }: { label: string; onClick: () => void }) {
  return (
    <Button type="button" size="sm" variant="outline" onClick={onClick}>
      <Plus className="h-3.5 w-3.5" aria-hidden="true" />
      {label}
    </Button>
  );
}
